import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, Input, ViewChild, inject } from '@angular/core';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';

@Component({
  standalone: true,
  selector: 'webstore-image-viewer',
  imports: [CommonModule],
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent implements AfterViewInit {
  private _document = inject<Document>(DOCUMENT);

  @ViewChild('image') image!: ElementRef;
  currentIndex = 0;
  IMAGE_URLS = IMAGE_URLS;
  previewImage: string = IMAGE_URLS.noImagePlaceholder;

  @Input() set imagesList(images: any[]) {
    if (Array.isArray(images)) {
      this._images = images.map((url: string) => ({
        image: url,
        thumbImage: url,
        alt: '',
        title: '',
        widthForSlider: 120
      }));

      this.previewImage = this._images?.length > 0 ? this._images[0]!.image : this.IMAGE_URLS.noImagePlaceholder;
    } else {
      this._images = [];
    }
  }
  get imagesList() {
    return this._images;
  }

  get images(): {
    image: string;
    thumbImage: string;
    alt: string;
    title: string;
    widthForSlider: number;
  }[] {
    return this._images;
  }

  private _images: {
    image: string;
    thumbImage: string;
    alt: string;
    title: string;
    widthForSlider: number;
  }[] = [];

  startXPosition = 0;
  startYPosition = 0;
  translateX = 0;

  ngAfterViewInit(): void {

    // Store references to the handlers so they can be removed if needed
    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      this.startXPosition = touch.clientX;
      this.startYPosition = touch.clientY;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const deltaX = touch.clientX - this.startXPosition;
      const deltaY = touch.clientY - this.startYPosition;

      // Only handle significant horizontal swipes
      if (Math.abs(deltaX) < 30 || Math.abs(deltaY / deltaX) > 0.5) return;

      // If you have a zoom/scale feature, replace 1 with the actual scale value if needed
      const currentScale = 1;

      if (currentScale === 1) {
        if (deltaX > 0) {
          this.prevImage();
        } else if (deltaX < 0) {
          this.nextImage();
        }
      } else {
        const transform = this.image.nativeElement.style.transform;
        const matrix = transform?.match(/matrix\(([^)]+)\)/)?.[1]?.split(',')?.map(parseFloat);
        const currentTranslateX = matrix ? matrix[4] : 0;
        if (this.translateX === currentTranslateX) {
          if (currentTranslateX > 0) {
            this.prevImage();
          } else if (currentTranslateX < 0) {
            this.nextImage();
          }
        }
        this.translateX = currentTranslateX;
      }
    };

    if (this.image.nativeElement) {
      this.image.nativeElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      this.image.nativeElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    }
  }

  nextImage(): void {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.scrollToThumbnail(this.currentIndex);
    this.previewImage = this.images[this.currentIndex]!.image;
  }

  prevImage(): void {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.scrollToThumbnail(this.currentIndex);
    this.previewImage = this.images[this.currentIndex]!.image;
  }

  updateActiveThumbnail(index: number, imageUrl: string): void {
    this.currentIndex = index;
    this.previewImage = imageUrl;
    this.scrollToThumbnail(index);
  }


  scrollToThumbnail(index: number): void {
    const thumbnailList = this._document.querySelector('.thumbnail-vertical-list') as HTMLElement;
    const thumbnails = this._document.querySelectorAll('.det-default-thumbnail-list-item');

    if (thumbnails.length > 0) {
      const selectedThumbnail = thumbnails[index] as HTMLElement;
      const extraOffset = selectedThumbnail.offsetHeight / 3;

      if (thumbnailList) {
        // Case 1: Scrolling down when there are more images below
        if (index >= 3 && index < thumbnails.length - 1) {
          const nextItem = thumbnails[index + 1] as HTMLElement;
          thumbnailList.scrollTo({ top: nextItem.offsetTop - extraOffset, behavior: 'smooth' });
        }
        // Case 2: Scrolling up when there are more images above
        else if (index <= thumbnails.length - 3 && index > 0) {
          const prevItem = thumbnails[index - 1] as HTMLElement;
          thumbnailList.scrollTo({ top: prevItem.offsetTop + extraOffset, behavior: 'smooth' });
        }
        // Case 3: Edge cases (first or last image)
        else {
          selectedThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }

}

